import cn from 'classnames';
import moment from 'moment-timezone';
import { useCallback, useState } from 'react';
import ReactDOM from 'react-dom';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  Checkbox,
  CustomThemeProvider,
  Loader,
  LoadingSkeleton,
  Tag,
  Tooltip,
  TooltipInteractionEnum,
  usePortal
} from 'sonata-react-components';

import './shareReportList.scss';

import { PromptModal } from '../PromptModal';

export type ShareReportListItem = {
  _id: string;
  name?: string;
  enabled: boolean;
  expirationDate?: string;
};

type Props = {
  activeItemId?: string;
  items: ShareReportListItem[] | null;
  loading?: boolean;
  onCopy: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleEnabled: (id: string, enabled: boolean) => void;
};

type ExpiryKey = 'expireInDays' | 'expireInDay' | 'expireInHours' | 'expireInHour' | 'expired';

const EXPIRY_MESSAGES: Record<ExpiryKey, { defaultMessage: string; id: string }> = {
  expireInDay: { defaultMessage: 'Expires in {count} day', id: 'shareReport.list.shareLinksList.expireInDay' },
  expireInDays: {
    defaultMessage: 'Expires in {count} days',
    id: 'shareReport.list.shareLinksList.expireInDays'
  },
  expireInHour: {
    defaultMessage: 'Expires in {count} hour',
    id: 'shareReport.list.shareLinksList.expireInHour'
  },
  expireInHours: {
    defaultMessage: 'Expires in {count} hours',
    id: 'shareReport.list.shareLinksList.expireInHours'
  },
  expired: { defaultMessage: 'Expired', id: 'shareReport.list.shareLinksList.expired' }
};

function diffInHours(expirationDate: string): number {
  return moment(expirationDate).utc().diff(moment.utc(), 'hours');
}

function hasExpired(expirationDate?: string): boolean {
  return !!expirationDate && diffInHours(expirationDate) <= 0;
}

function getExpiryKey(expirationDate: string): ExpiryKey {
  const hoursToExpire = diffInHours(expirationDate);
  const daysToExpire = hoursToExpire / 24;
  if (daysToExpire > 1) {
    return 'expireInDays';
  }
  if (daysToExpire === 1) {
    return 'expireInDay';
  }
  if (hoursToExpire > 1) {
    return 'expireInHours';
  }
  if (hoursToExpire === 1) {
    return 'expireInHour';
  }
  return 'expired';
}

export default function ShareReportList({
  activeItemId,
  items,
  loading,
  onCopy,
  onDelete,
  onToggleEnabled
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const targetBodyPrompt = usePortal('shareReportListPrompt');

  const [idToDelete, setIdToDelete] = useState<string | null>(null);

  const onCancelDelete = useCallback(() => {
    setIdToDelete(null);
  }, []);

  const onConfirmDelete = useCallback(() => {
    if (!idToDelete) {
      return;
    }
    const id = idToDelete;
    setIdToDelete(null);
    onDelete(id);
  }, [idToDelete, onDelete]);

  const renderItem = (item: ShareReportListItem) => (
    <div
      className={cn('sharedReportList__item', {
        'sharedReportList__item--active': item._id === activeItemId
      })}
      id={`shared-report-list-item-${item._id}`}
      key={item._id}
    >
      <div className="sharedReportList__itemInfo">
        <div className="sharedReportList__nameRow">
          <Tooltip
            config={{
              arrow: true,
              interactions: [TooltipInteractionEnum.hover],
              placement: 'bottom',
              strategy: 'fixed',
              text: item.name ?? ''
            }}
          >
            <div className="sharedReportList__itemName">{item.name}</div>
          </Tooltip>
          {item.expirationDate && (
            <Tag
              className="sharedReportList__tag"
              id={`shared-report-expiry-${item._id}`}
              status={hasExpired(item.expirationDate) ? 'disabled' : 'information'}
              tiny
            >
              {formatMessage(EXPIRY_MESSAGES[getExpiryKey(item.expirationDate)], {
                count: Math.floor(diffInHours(item.expirationDate) / 24) || diffInHours(item.expirationDate)
              })}
            </Tag>
          )}
        </div>
      </div>
      <div className="sharedReportList__itemActions">
        <Checkbox
          active={item.enabled}
          disabled={hasExpired(item.expirationDate)}
          id={`shared-report-list-item-${item._id}-enable-toggle`}
          isSwitch
          labelText={formatMessage({
            defaultMessage: 'Active',
            id: 'shareReport.list.shareLink.active'
          })}
          onClickAction={(_id: string, checked: boolean) => onToggleEnabled(item._id, checked)}
          tiny
        />
        <ButtonUI
          icon="far fa-trash"
          id={`shared-report-list-item-${item._id}-delete`}
          onClick={() => setIdToDelete(item._id)}
          size="ultraSmall"
          type="secondary"
        />
        <ButtonUI
          disabled={hasExpired(item.expirationDate)}
          icon="far fa-copy"
          iconPosition="right"
          id={`shared-report-list-item-${item._id}-copy`}
          onClick={() => onCopy(item._id)}
          size="ultraSmall"
          text={formatMessage({
            defaultMessage: 'Copy link',
            id: 'shareReport.list.shareLink.copyLink'
          })}
        />
      </div>
    </div>
  );

  return (
    <div className="sharedReportList">
      {loading && <Loader showBackground type="landing" variant="default" />}
      {items ? (
        items.length ? (
          <div className="sharedReportList__items">{items.map(renderItem)}</div>
        ) : (
          <div className="sharedReportList__empty">
            {formatMessage({
              defaultMessage: 'No links of this reporting have been generated yet',
              id: 'shareReport.list.shareLink.emptyList'
            })}
          </div>
        )
      ) : (
        <LoadingSkeleton count={3} height={55} />
      )}
      {idToDelete &&
        ReactDOM.createPortal(
          <CustomThemeProvider themeContrast="light">
            <PromptModal
              cancelCallback={onCancelDelete}
              confirmCallback={onConfirmDelete}
              texts={{
                buttons: {
                  cancel: formatMessage({
                    defaultMessage: 'Cancel',
                    id: 'common.cancel'
                  }),
                  confirm: formatMessage({
                    defaultMessage: 'Accept',
                    id: 'common.accept'
                  })
                },
                description: formatMessage({
                  defaultMessage:
                    'If you delete this shared report, those with the link will lose access.This change cannot be reversed.',
                  id: 'sharedReport.shareLink.deletionPrompt.description'
                }),
                title: formatMessage({
                  defaultMessage: 'Are you sure you want to delete this shared report?',
                  id: 'sharedReport.shareLink.deletionPrompt.title'
                })
              }}
            />
          </CustomThemeProvider>,
          targetBodyPrompt
        )}
    </div>
  );
}
